import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';
import swal from 'sweetalert';
import Select from 'react-select';

import {
    useDeleteUserSeatingProfileMutation,
    useEditUserSeatingProfileMutation,
    useGetSystemSeatsForClientRouteTimeQuery,
    useGetUserSeatingProfileByIdQuery,
} from '../../Features/seatsSlice';

import LoadingBar from 'react-top-loading-bar';
import { useIsOnline } from 'react-use-is-online';

import {
    ViewGridIcon,
    CollectionIcon,
    TrashIcon,
    ArrowLeftIcon,
} from '@heroicons/react/outline';

function EditUserSeatingProfileView() {
    const navigate = useNavigate();
    const { profile_id } = useParams();
    const { isOnline } = useIsOnline();

    const [progress, setProgress] = useState(0);

    const [input, setInput] = useState({
        name: '',
        id: '',
    });

    const [seatsArray, setSeatsArray] = useState([]);

    const [errors, setErrors] = useState({
        name: '',
        seats: '',
    });

    const {
        data: seatingProfile,
        isLoading,
        isSuccess,
        isError,
    } = useGetUserSeatingProfileByIdQuery(profile_id);

    const {
        data: seats,
        isLoading: isLoadingSeats,
        isSuccess: isSuccessSeats,
    } = useGetSystemSeatsForClientRouteTimeQuery();

    const [editSeatingProfile, { isLoading: isSaving }] =
        useEditUserSeatingProfileMutation();

    const [deleteSeatingProfile, { isLoading: isDeleting }] =
        useDeleteUserSeatingProfileMutation();

    /*
     * Load profile information
     */
    useEffect(() => {
        setProgress(30);

        if (isLoading || isLoadingSeats) {
            setProgress(80);
        }

        if (isSuccess && seatingProfile?.data) {
            setInput(seatingProfile.data);
        }

        if (isSuccess && isSuccessSeats) {
            setProgress(100);
        }

        if (isError) {
            setProgress(100);
        }
    }, [
        seatingProfile,
        isLoading,
        isSuccess,
        isError,
        isLoadingSeats,
        isSuccessSeats,
    ]);

    /*
     * Set existing seats after both the profile
     * and system seats have loaded.
     *
     * This assumes the profile's seats contain
     * the same values used by the Select options.
     */
    useEffect(() => {
        if (
            isSuccess &&
            isSuccessSeats &&
            seatingProfile?.data &&
            Array.isArray(seats)
        ) {
            const existingSeats = seatingProfile.data.seats || [];

            const selectedSeats = seats.filter((seat) => {
                return existingSeats.some((existingSeat) => {
                    if (
                        existingSeat?.value !== undefined &&
                        seat?.value !== undefined
                    ) {
                        return existingSeat.value === seat.value;
                    }

                    if (
                        existingSeat?.id !== undefined &&
                        seat?.id !== undefined
                    ) {
                        return existingSeat.id === seat.id;
                    }

                    return existingSeat === seat;
                });
            });

            setSeatsArray(selectedSeats);
        }
    }, [
        seatingProfile,
        seats,
        isSuccess,
        isSuccessSeats,
    ]);

    const handleChange = (e) => {
        const { name, value } = e.target;

        setInput((prev) => ({
            ...prev,
            [name]: value,
        }));

        setErrors((prev) => ({
            ...prev,
            [name]: '',
        }));
    };

    const handleSeatsChange = (selectedSeats) => {
        setSeatsArray(selectedSeats || []);

        setErrors((prev) => ({
            ...prev,
            seats: '',
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setErrors({
            name: '',
            seats: '',
        });

        if (!input.name?.trim()) {
            setErrors((prev) => ({
                ...prev,
                name: 'Name is required',
            }));

            return;
        }

        if (!seatsArray || seatsArray.length === 0) {
            setErrors((prev) => ({
                ...prev,
                seats: 'Please select at least one seat',
            }));

            return;
        }

        const data = {
            name: input.name,
            seating_profile_id: profile_id,
            seats: seatsArray,
        };

        try {
            setProgress(80);

            const resp = await editSeatingProfile(data).unwrap();

            if (resp?.success === true) {
                setProgress(100);

                swal(
                    'Success',
                    resp?.message || 'Seating profile updated successfully',
                    'success'
                );

                navigate(-1);
            } else {
                setProgress(100);
            }
        } catch (err) {
            setProgress(100);

            if (err?.data?.errors) {
                setErrors({
                    name: err.data.errors.name || '',
                    seats: err.data.errors.seats || '',
                });
            } else if (err?.data?.error) {
                swal(
                    'Error',
                    err.data.error,
                    'error'
                );
            } else {
                swal(
                    'Error',
                    'Something went wrong. Please try again.',
                    'error'
                );
            }
        }
    };

    const handleDelete = async (id) => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        const confirmed = window.confirm(
            'Are you sure you want to delete this profile?'
        );

        if (!confirmed) {
            return;
        }

        try {
            setProgress(80);

            const resp = await deleteSeatingProfile(id).unwrap();

            if (resp?.success === true) {
                setProgress(100);

                swal(
                    'Deleted',
                    resp?.message || 'Seating profile deleted successfully',
                    'success'
                );

                return navigate(-1);
            }

            setProgress(100);
        } catch (err) {
            setProgress(100);

            swal(
                'Error',
                err?.data?.error ||
                    err?.message ||
                    'Unable to delete seating profile.',
                'error'
            );
        }
    };

    const back = () => {
        if (!isOnline) {
            return swal(
                'No Internet connection',
                'Please check Internet connection and try again',
                'error'
            );
        }

        setProgress(100);
        navigate(-1);
    };

    /*
     * react-select styling
     */
    const isDarkMode =
        document.documentElement.classList.contains('dark');

    const selectStyles = {
        control: (provided, state) => ({
            ...provided,
            minHeight: '42px',
            borderRadius: '8px',
            borderColor: state.isFocused
                ? '#80b643'
                : isDarkMode
                    ? '#374151'
                    : '#d1d5db',
            boxShadow: state.isFocused
                ? '0 0 0 1px #80b643'
                : 'none',
            backgroundColor: isDarkMode
                ? '#1f2937'
                : '#ffffff',
            '&:hover': {
                borderColor: '#80b643',
            },
        }),

        menu: (provided) => ({
            ...provided,
            zIndex: 50,
            backgroundColor: isDarkMode
                ? '#1f2937'
                : '#ffffff',
        }),

        menuList: (provided) => ({
            ...provided,
            maxHeight: '220px',
        }),

        option: (provided, state) => ({
            ...provided,
            fontSize: '14px',
            backgroundColor: state.isSelected
                ? '#80b643'
                : state.isFocused
                    ? 'rgba(128, 182, 67, 0.10)'
                    : 'transparent',
            color: state.isSelected
                ? '#ffffff'
                : isDarkMode
                    ? '#ffffff'
                    : '#374151',
            cursor: 'pointer',
        }),

        multiValue: (provided) => ({
            ...provided,
            backgroundColor: 'rgba(128, 182, 67, 0.12)',
            borderRadius: '6px',
        }),

        multiValueLabel: (provided) => ({
            ...provided,
            color: '#80b643',
            fontSize: '12px',
            fontWeight: '500',
        }),

        multiValueRemove: (provided) => ({
            ...provided,
            color: '#80b643',
            ':hover': {
                backgroundColor: '#80b643',
                color: '#ffffff',
            },
        }),

        placeholder: (provided) => ({
            ...provided,
            color: '#9ca3af',
            fontSize: '14px',
        }),

        input: (provided) => ({
            ...provided,
            color: isDarkMode
                ? '#ffffff'
                : '#374151',
            fontSize: '14px',
        }),

        singleValue: (provided) => ({
            ...provided,
            color: isDarkMode
                ? '#ffffff'
                : '#374151',
            fontSize: '14px',
        }),

        noOptionsMessage: (provided) => ({
            ...provided,
            fontSize: '13px',
            color: '#9ca3af',
        }),
    };

    return (
        <div className="w-full min-h-screen bg-gray-100 dark:bg-gray-950 pb-12">

            <LoadingBar
                color="#80b643"
                height="4px"
                progress={progress}
                onLoaderFinished={() => setProgress(0)}
            />

            <div className="w-full px-4 sm:px-6 lg:px-8 pt-6">

                {/* PAGE HEADER */}
                <div className="flex items-center gap-3 mb-6">

                    <div className="w-11 h-11 rounded-xl bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                        <ViewGridIcon className="w-6 h-6 text-[#80b643]" />
                    </div>

                    <div>
                        <h1 className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white">
                            Edit Seating Profile
                        </h1>

                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            Update your seating profile
                        </p>
                    </div>

                </div>

                {/* MAIN CARD */}
                <div className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl shadow-sm overflow-hidden">

                    {/* CARD HEADER */}
                    <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-gray-800">

                        <div className="flex items-center gap-3">

                            <div className="w-9 h-9 rounded-lg bg-[#80b643]/10 dark:bg-[#80b643]/20 flex items-center justify-center">
                                <CollectionIcon className="w-5 h-5 text-[#80b643]" />
                            </div>

                            <div>
                                <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
                                    Seating Profile Information
                                </h2>

                                <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                    Update the profile name and selected seats
                                </p>
                            </div>

                        </div>

                    </div>

                    {/* LOADING PROFILE */}
                    {isLoading && (
                        <div className="flex flex-col items-center justify-center py-16">

                            <div className="w-8 h-8 border-2 border-gray-200 dark:border-gray-700 border-t-[#80b643] rounded-full animate-spin"></div>

                            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
                                Loading seating profile...
                            </p>

                        </div>
                    )}

                    {/* ERROR */}
                    {isError && !isLoading && (
                        <div className="flex flex-col items-center justify-center py-16 px-4 text-center">

                            <div className="w-14 h-14 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
                                <ViewGridIcon className="w-7 h-7 text-gray-400" />
                            </div>

                            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                                Unable to load seating profile
                            </h3>

                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                                We could not retrieve this seating profile.
                            </p>

                            <button
                                type="button"
                                onClick={back}
                                className="mt-5 inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                <ArrowLeftIcon className="w-4 h-4" />
                                Go Back
                            </button>

                        </div>
                    )}

                    {/* FORM */}
                    {isSuccess && seatingProfile?.data && (
                        <form
                            onSubmit={handleSubmit}
                            autoComplete="off"
                            className="p-4 sm:p-6"
                        >

                            <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">

                                {/* NAME */}
                                <div>

                                    <label
                                        htmlFor="name"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                    >
                                        Profile Name
                                    </label>

                                    <input
                                        type="text"
                                        name="name"
                                        id="name"
                                        value={input.name || ''}
                                        onChange={handleChange}
                                        placeholder="Enter seating profile name"
                                        disabled={isSaving || isDeleting}
                                        className={`w-full px-3 py-2.5 rounded-lg bg-white dark:bg-gray-800 text-sm text-gray-700 dark:text-white border ${
                                            errors.name
                                                ? 'border-red-400 focus:border-red-400 focus:ring-red-400'
                                                : 'border-gray-200 dark:border-gray-700 focus:border-[#80b643] focus:ring-[#80b643]'
                                        } focus:ring-1 outline-none transition disabled:opacity-60`}
                                    />

                                    {errors.name && (
                                        <p className="mt-1.5 text-xs font-medium text-red-500">
                                            {errors.name}
                                        </p>
                                    )}

                                </div>

                                {/* SEATS */}
                                <div>

                                    <label
                                        htmlFor="seats"
                                        className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2"
                                    >
                                        Select Seats
                                    </label>

                                    <Select
                                        inputId="seats"
                                        options={seats || []}
                                        value={seatsArray}
                                        isMulti
                                        isSearchable
                                        isLoading={isLoadingSeats}
                                        isDisabled={isSaving || isDeleting}
                                        onChange={handleSeatsChange}
                                        placeholder={
                                            isLoadingSeats
                                                ? 'Loading seats...'
                                                : 'Select seats'
                                        }
                                        styles={selectStyles}
                                        noOptionsMessage={() =>
                                            'No seats available'
                                        }
                                    />

                                    {errors.seats && (
                                        <p className="mt-1.5 text-xs font-medium text-red-500">
                                            {errors.seats}
                                        </p>
                                    )}

                                </div>

                            </div>

                            {/* DELETE SECTION */}
                            <div className="mt-8 pt-5 border-t border-gray-200 dark:border-gray-800">

                                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">

                                    <div className="flex items-start gap-3">

                                        <div className="w-9 h-9 rounded-lg bg-red-50 dark:bg-red-900/20 flex items-center justify-center flex-shrink-0">
                                            <TrashIcon className="w-5 h-5 text-red-500" />
                                        </div>

                                        <div>
                                            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">
                                                Delete Profile
                                            </h3>

                                            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                                                You can permanently delete this profile.
                                            </p>
                                        </div>

                                    </div>

                                    <button
                                        type="button"
                                        onClick={() =>
                                            handleDelete(input.id)
                                        }
                                        disabled={isSaving || isDeleting}
                                        className="inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg border border-red-200 dark:border-red-900/40 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 text-sm font-medium transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed w-full sm:w-auto"
                                    >
                                        <TrashIcon className="w-4 h-4" />

                                        {isDeleting
                                            ? 'Deleting...'
                                            : 'Delete Profile'}
                                    </button>

                                </div>

                            </div>

                            {/* ACTION BUTTONS */}
                            <div className="mt-6 pt-5 border-t border-gray-200 dark:border-gray-800">

                                <div className="flex flex-col-reverse sm:flex-row sm:justify-end gap-3">

                                    <button
                                        type="button"
                                        onClick={back}
                                        disabled={isSaving || isDeleting}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 transition-all duration-200 w-full sm:w-auto disabled:opacity-50"
                                    >
                                        <ArrowLeftIcon className="w-4 h-4" />
                                        Cancel
                                    </button>

                                    <button
                                        type="submit"
                                        disabled={isSaving || isDeleting}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-xl bg-[#80b643] hover:bg-[#719f3a] text-white text-sm font-medium shadow-sm transition-all duration-200 w-full sm:w-auto disabled:opacity-60 disabled:cursor-not-allowed"
                                    >
                                        {isSaving ? (
                                            <>
                                                <svg
                                                    className="animate-spin h-4 w-4 text-white"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <circle
                                                        className="opacity-25"
                                                        cx="12"
                                                        cy="12"
                                                        r="10"
                                                        stroke="currentColor"
                                                        strokeWidth="4"
                                                    />

                                                    <path
                                                        className="opacity-75"
                                                        fill="currentColor"
                                                        d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
                                                    />
                                                </svg>

                                                Saving...
                                            </>
                                        ) : (
                                            'Save Changes'
                                        )}
                                    </button>

                                </div>

                            </div>

                        </form>
                    )}

                </div>

            </div>

        </div>
    );
}

export default EditUserSeatingProfileView;